/**
 * Materials API Service
 * Handles materials, categories, and mand items
 */

import { get, post, patch, put } from './client';

// =============================================================================
// File-share materials (central + child-specific)
// =============================================================================
//
// These wrap the file-store endpoints in backend/app/api/materials.py. The
// shape is { files: [{ name, path, url, category?, size }], total }.
// `category` is only set for central materials (folder under common/).

/**
 * List central (org-shared) materials from the file share.
 * @returns {Promise<{files: Array, total: number}>}
 */
export const listCentralFileMaterials = () => get('/materials/list');

/**
 * List child-specific materials from the file share. Files live under
 * material-repo/{childId}/ — no category folders.
 * @param {string} childId
 * @returns {Promise<{files: Array, total: number}>}
 */
export const listChildFileMaterials = (childId) => get(`/materials/child/${encodeURIComponent(childId)}/list`);

/**
 * Fetch material metadata (custom display names, descriptions, tags) keyed by
 * file path or filename. The supervisor's Material Repository writes here when
 * a material is named/edited; downstream callers merge this onto the file list
 * to surface display names like "Daddy" instead of raw filenames like
 * "father.jpg".
 * @returns {Promise<{metadata: Record<string, {name?: string, category?: string, description?: string, tags?: string[]}>}>}
 */
export const getMaterialsMetadata = () => get('/materials/metadata');

/**
 * Update a material's metadata (name/category/description/tags), keyed by file path.
 * Used to edit description & tags in place without touching the image or filename.
 * @param {string} filePath - path/key returned by the list (e.g. "common/food/apple.jpg")
 * @param {{filename: string, name: string, category: string, description: string, tags: string[]}} metadata
 */
export const updateMaterialMetadata = (filePath, metadata) => put(`/materials/metadata/${encodeURIComponent(filePath)}`, metadata);

/**
 * Rename a child-specific material file. The displayed material name is
 * derived from the filename on disk, so persisting a rename = physically
 * renaming the file (the new name keeps the original extension).
 * @param {string} childId
 * @param {string} filePath - The full relative path returned by the list endpoint (e.g. "CHD-260511-ZLEL/father.jpg")
 * @param {string} newName - The new human-readable name (e.g. "Daddy")
 * @returns {Promise<{filename: string, path: string, url: string}>}
 */
export const renameChildFileMaterial = (childId, filePath, newName) =>
  patch(`/materials/child/${encodeURIComponent(childId)}/rename/${filePath}`, { name: newName });

// =============================================================================
// Material Categories
// =============================================================================

/**
 * List material categories
 * @returns {Promise} List of categories
 */
export const listCategories = () => get('/materials/categories');

// =============================================================================
// Materials
// =============================================================================

/**
 * List materials with optional filtering
 * @param {Object} params - Query parameters (category_id, org_id)
 * @returns {Promise} List of materials
 */
export const listMaterials = (params = {}) => get('/materials', params);

/**
 * Create a material
 * @param {Object} data - Material data
 * @returns {Promise} Created material
 */
export const createMaterial = (data) => post('/materials', data);

// =============================================================================
// Mand Items
// =============================================================================

/**
 * List mand items from the reference catalog
 * @param {Object} params - Query parameters (category, org_id)
 * @returns {Promise} List of mand items
 */
export const listMandItems = (params = {}) => get('/materials/mand-items', params);

/**
 * Create a mand item
 * @param {Object} data - Mand item data
 * @returns {Promise} Created mand item
 */
export const createMandItem = (data) => post('/materials/mand-items', data);

export default {
  listCategories,
  listMaterials,
  createMaterial,
  listMandItems,
  createMandItem
};
